import { parseUtcMs } from './flock-telemetry.utils';
import {
  DisagreementFlag,
  OwedItem,
  SeatHygiene,
  SeatWorkQueue,
} from './work-queue.model';

/**
 * Pure display logic for the work-queue pane (WI 30957). No HTTP, no Angular:
 * turns the bridge's raw per-seat rows into classified, sorted display rows and
 * the headline summary. Unit-tested; the component and Kendo grid render the
 * output of these functions and never re-derive state themselves.
 */

/** Primary per-seat state. `dead` is liveness-first: a not-alive seat reads dead
 * regardless of its self-reported `active`, because the model-call liveness signal
 * cannot be forged by a corpse (see WorkQueueEndpoints). `available` is the one
 * clean "safe to hand work" state the endpoint already computed. */
export type SeatBand = 'available' | 'active' | 'parked' | 'idle' | 'dead';

export interface SeatRow {
  instanceId: string;
  band: SeatBand;
  active: boolean;
  modelAlive: boolean;
  /** Resolved liveness tier from the bridge (WI 31104); null on an old bridge. */
  livenessBand: 'dead' | 'idle' | 'active' | null;
  livenessReason: string | null;
  /** Whole minutes since the last model call; null when the seat never sent.
   * Drives the 'idle Nm' sub-note for liveness-idle seats. */
  modelIdleMinutes: number | null;
  /** Card header fields (WI 31103). Usage percentages are null for "no reading". */
  accountUpn: string | null;
  usage5hPct: number | null;
  usage5hResetAt: string | null;
  usageWeekPct: number | null;
  usageWeekResetAt: string | null;
  /** The sooner of the two usage resets that lies in the FUTURE; null when neither does. */
  nextResetAt: string | null;
  /** ADO link for the in-flight ticket; null when the ticket carries no WI number. */
  ticketUrl: string | null;
  available: boolean;
  currentTicketId: string | null;
  currentTicketTitle: string | null;
  currentActivity: string | null;
  parkedReason: string | null;
  owedItems: OwedItem[];
  owedCount: number;
  blockingOwedCount: number;
  /** Age in minutes of the oldest owed item that carries a `since`; null when none do. */
  oldestOwedMinutes: number | null;
  openInboundCount: number;
  overdueInboundCount: number;
  /** Minutes the oldest open inbound is PAST due (positive) or until due (negative); null when none. */
  oldestInboundMinutes: number | null;
  reportedAgeMinutes: number | null;
  disagreement: DisagreementFlag[];
  hasDisagreement: boolean;
  /** disagreement codes joined for a compact at-a-glance cell, e.g. "active-but-silent, idle-but-owes". */
  disagreementCodes: string;
  /** The seat's current-ticket/activity claim is no longer trustworthy (WI 31375,
   * part a): a `ticket-closed` (ADO says the WI is Done) or `stale-report` flag is
   * present. The pane greys and strikes the ticket + activity so a finished or stale
   * claim never reads as live work. See {@link ticketClaimIsStale}. */
  ticketStale: boolean;
  /** Soft-deleted seat (WI 31108). Present only when the Show-retired toggle
   * opted in via ?showRetired=true; renders greyed, grouped after dead. */
  retired: boolean;
  retiredReason: string | null;
  /** Whole days since retirement; null when retiredAt is missing/unparseable. */
  retiredAgeDays: number | null;
  /** Bridge-computed code-seat flag (WI 31145). NULL means the bridge did not
   * send it (pre-31145 deploy); false is a POSITIVE "not a code seat". The two
   * must stay distinct (grok 2790 N2): the empty-state's "redeploy the bridge"
   * copy is a lie when the fleet genuinely has no code seats in view. */
  isCodeSeat: boolean | null;
  /** External collaborator seat (US 37271); the pane tags it. False on an old bridge. */
  isExternal: boolean;
  /** Owed-queue hygiene passed through from the bridge (US 35529); null on an old bridge. */
  hygiene?: SeatHygiene | null;
}

/** Seat-scope filter for the pane (WI 31145): everything, or only the
 * builder/reviewer code seats (bridge-classified, first-ryn included). */
export type SeatScope = 'all' | 'code';

/** Pure scope filter. 'all' is identity; 'code' keeps bridge-flagged code
 * seats only (null, flag not served, does not pass: an old bridge yields an
 * empty scope plus the explicit flag-not-served empty-state, never a guess).
 * Retirement is orthogonal: a retired code seat stays visible under 'code'
 * when the Show-retired toggle fetched it. */
export function filterSeatRows(rows: SeatRow[], scope: SeatScope): SeatRow[] {
  return scope === 'code' ? rows.filter((r) => r.isCodeSeat === true) : rows;
}

/** True when ANY row carries a served code-seat flag (true or false). All-null
 * means the bridge predates WI 31145, which is the only case where the pane may
 * say "redeploy the bridge" (grok 2790 N2). */
export function codeFlagServed(rows: SeatRow[]): boolean {
  return rows.some((r) => r.isCodeSeat !== null);
}

export interface QueueSummary {
  totalSeats: number;
  activeSeats: number;
  availableSeats: number;
  seatsInDisagreement: number;
  /** The stall the pane exists to surface: not alive but holding a blocking owed item. */
  deadSeatsOwingBlocking: number;
  /** Soft-deleted rows in view (WI 31108); non-zero only with Show retired on.
   * Excluded from every other number above so the fleet counts stay stable
   * whether or not the lens is open. */
  retiredSeats: number;
}

/** Defensive: some JSON serializers emit ints as strings. Coerce, NaN -> 0. */
function num(v: number | string | null | undefined): number {
  if (v == null) return 0;
  const n = typeof v === 'string' ? Number(v) : v;
  return Number.isFinite(n) ? n : 0;
}

/** Whole minutes between an ISO instant and now. null when the stamp is missing
 * or unparseable, so a bad timestamp never renders as "0m ago". Positive means
 * the instant is in the PAST (an age); callers that want overdue-vs-pending sign
 * it themselves via {@link inboundMinutes}. Parses via {@link parseUtcMs} so a SQL
 * wall-clock stamp with NO zone offset is read as UTC, not viewer-local (the
 * endpoint's DateTimes come from SYSUTCDATETIME and serialize without a Z). */
export function ageMinutes(iso: string | null, nowMs: number): number | null {
  const t = parseUtcMs(iso);
  if (t == null) return null;
  return Math.round((nowMs - t) / 60_000);
}

/** Signed minutes for an SLA-style due stamp: positive = overdue (past), negative
 * = still pending (future). Same null contract as {@link ageMinutes}. */
export function inboundMinutes(
  iso: string | null,
  nowMs: number
): number | null {
  return ageMinutes(iso, nowMs);
}

/** The oldest (largest) age among owed items carrying a `since`; null when none do. */
export function oldestOwedMinutes(
  owed: OwedItem[],
  nowMs: number
): number | null {
  let oldest: number | null = null;
  for (const o of owed ?? []) {
    const age = ageMinutes(o.since, nowMs);
    if (age != null && (oldest == null || age > oldest)) oldest = age;
  }
  return oldest;
}

/** Primary band. Order is deliberate: liveness (dead) outranks any self-report,
 * then parked, then the endpoint's clean `available`, then active, then idle.
 *
 * WI 31104 (Tim, call 6d006280): DEAD means no running process, full stop. The
 * bridge resolves that as livenessBand ('dead' | 'idle' | 'active'), so the pane
 * consumes the resolved band instead of re-deriving death from modelAlive. A
 * pre-31104 bridge sends no livenessBand; only then does the modelAlive
 * fallback apply (same populations: modelAlive=false === band 'dead'). */
/** Disagreement codes that mean the seat's CURRENT-TICKET/ACTIVITY claim is no
 * longer trustworthy, so the pane greys and strikes it (WI 31375, part a):
 *   - ticket-closed: the ADO work item reconciled to Done/Closed; the work is finished.
 *   - stale-report: the seat is still making model calls but its intent report is old.
 * `report-past-death` is deliberately NOT here: that seat is already band 'dead' and
 * the whole card is de-emphasized, so striking the ticket would add nothing. */
const TICKET_STALE_CODES = new Set(['ticket-closed', 'stale-report']);

/** True when the seat's ticket/activity claim should render greyed + struck: a
 * ticket-closed or stale-report disagreement flag is present. Pure; the retirement
 * override is applied by the caller (a retired seat's flags are history, {@link rowClasses}). */
export function ticketClaimIsStale(disagreement: DisagreementFlag[]): boolean {
  return (disagreement ?? []).some((d) => TICKET_STALE_CODES.has(d.code));
}

export function classifyBand(seat: SeatWorkQueue): SeatBand {
  const dead =
    seat.livenessBand != null ? seat.livenessBand === 'dead' : !seat.modelAlive;
  if (dead) return 'dead';
  if (seat.parkedReason != null) return 'parked';
  if (seat.available) return 'available';
  if (seat.active) return 'active';
  return 'idle';
}

/** Usage-bar severity for a window percentage. `none` (null pct) renders the
 * grey "no reading" bar (WI 31103): a missing reading must never look measured. */
export function usageBand(
  pct: number | null
): 'none' | 'ok' | 'warn' | 'danger' {
  if (pct == null) return 'none';
  if (pct >= 80) return 'danger';
  if (pct >= 50) return 'warn';
  return 'ok';
}

/** ADO deep link for a self-reported ticket ref like "WI 31091" / "Bug 30725".
 * Null when no work-item number is present (Tim's clickable-ticket rule; a
 * non-numeric ref has nowhere to link). */
export function workItemUrl(ticketId: string | null): string | null {
  if (!ticketId) return null;
  const edit = (n: string): string =>
    `https://dev.azure.com/universalplantdev/UPS1_Workspace/_workitems/edit/${n}`;
  // A work-item-prefixed number links even in a mixed ref ('WI 31103 (PR 2766)').
  const prefixed = ticketId.match(
    /^\s*(?:ADO\s+)?(?:WI|Bug|Task|Feature|Story|US)\s*#?(\d{4,6})\b/i
  );
  if (prefixed) return edit(prefixed[1]);
  // A PR-shaped ref is a pull request, not a work item: deep-linking its number
  // into _workitems opens the WRONG ARTIFACT (grok N2, PR 2766).
  if (/\bPR\s*#?\d/i.test(ticketId)) return null;
  // Preserve legacy bare WI IDs, but never reinterpret Halo/URL/message
  // references merely because they contain a number of the same length.
  const bare = ticketId.match(/^\s*(\d{4,6})\s*$/);
  return bare ? edit(bare[1]) : null;
}

/** The sooner of the two usage resets that is still in the FUTURE. A past
 * stamp is an expired window, not a "next reset". */
export function nextResetAt(
  fiveHr: string | null,
  weekly: string | null,
  nowMs: number
): string | null {
  const future = [fiveHr, weekly]
    .map((iso) => ({ iso, t: parseUtcMs(iso) }))
    .filter((x): x is { iso: string; t: number } => x.t != null && x.t > nowMs);
  if (future.length === 0) return null;
  future.sort((a, b) => a.t - b.t);
  return future[0].iso;
}

function toRow(seat: SeatWorkQueue, nowMs: number): SeatRow {
  const owed = seat.owedItems ?? [];
  const disagreement = seat.disagreement ?? [];
  return {
    instanceId: seat.instanceId,
    band: classifyBand(seat),
    active: seat.active,
    modelAlive: seat.modelAlive,
    livenessBand: seat.livenessBand ?? null,
    livenessReason: seat.livenessReason ?? null,
    modelIdleMinutes: ageMinutes(seat.lastModelCallAt, nowMs),
    accountUpn: seat.accountUpn ?? null,
    usage5hPct: seat.usage5hPct ?? null,
    usage5hResetAt: seat.usage5hResetAt ?? null,
    usageWeekPct: seat.usageWeekPct ?? null,
    usageWeekResetAt: seat.usageWeekResetAt ?? null,
    nextResetAt: nextResetAt(
      seat.usage5hResetAt ?? null,
      seat.usageWeekResetAt ?? null,
      nowMs
    ),
    ticketUrl: workItemUrl(seat.currentTicketId),
    available: seat.available,
    currentTicketId: seat.currentTicketId,
    currentTicketTitle: seat.currentTicketTitle,
    currentActivity: seat.currentActivity,
    parkedReason: seat.parkedReason,
    owedItems: owed,
    owedCount: owed.length,
    blockingOwedCount: owed.filter((o) => o.blocking).length,
    oldestOwedMinutes: oldestOwedMinutes(owed, nowMs),
    openInboundCount: num(seat.openInboundCount),
    overdueInboundCount: num(seat.overdueInboundCount),
    oldestInboundMinutes: inboundMinutes(seat.oldestInboundDueAt, nowMs),
    reportedAgeMinutes: ageMinutes(seat.reportedAt, nowMs),
    disagreement,
    hasDisagreement: disagreement.length > 0,
    disagreementCodes: disagreement.map((d) => d.code).join(', '),
    ticketStale: ticketClaimIsStale(disagreement),
    retired: seat.retired ?? false,
    retiredReason: seat.retiredReason ?? null,
    retiredAgeDays: retiredAgeDays(seat.retiredAt ?? null, nowMs),
    isCodeSeat: seat.isCodeSeat ?? null,
    isExternal: seat.isExternal ?? false,
    hygiene: seat.hygiene ?? null,
  };
}

/** Whole days since retirement (floor, so "0d" means today). Null when the
 * stamp is missing or unparseable, same contract as {@link ageMinutes}. */
export function retiredAgeDays(
  retiredAt: string | null,
  nowMs: number
): number | null {
  const mins = ageMinutes(retiredAt, nowMs);
  return mins == null ? null : Math.floor(mins / 1440);
}

/** Ordinal compare so JS sort matches the bridge's StringComparer.Ordinal
 * tiebreak (stable, locale-independent order). */
function compareOrdinal(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Classify every seat and sort problems to the top: seats in disagreement first
 * (the lens the pane exists for), then not-alive seats, then most-overdue, then
 * most-owed, then stable by id. The UI order never depends on the server's sort.
 */
export function buildSeatRows(
  seats: SeatWorkQueue[],
  nowMs: number
): SeatRow[] {
  return (seats ?? [])
    .map((s) => toRow(s, nowMs))
    .sort(
      (a, b) =>
        Number(a.retired) - Number(b.retired) ||
        // Retired seats sort LAST regardless of every other signal: they are
        // history, not fleet, and must never outrank a live problem (WI 31108).
        Number(b.hasDisagreement) - Number(a.hasDisagreement) ||
        Number(!b.modelAlive) - Number(!a.modelAlive) ||
        b.overdueInboundCount - a.overdueInboundCount ||
        b.owedCount - a.owedCount ||
        compareOrdinal(a.instanceId, b.instanceId)
    );
}

/** Kendo row classes for the table view. Retirement SUPPRESSES the disagreement
 * highlight (grok N2, PR 2786): a retired seat's stale self-report flags are
 * history, and painting them as an incident contradicts the summary strip and
 * the State cell, which both already treat retired as excluded/overriding. */
export function rowClasses(row: SeatRow): Record<string, boolean> {
  return {
    'wq-row--disagreement': row.hasDisagreement && !row.retired,
    // Grey + strike a stale/closed ticket claim (WI 31375), suppressed on a retired
    // row for the same reason the disagreement highlight is: its flags are history.
    'wq-row--ticket-stale': row.ticketStale && !row.retired,
    'wq-row--retired': row.retired,
    [`wq-row--${row.band}`]: true,
  };
}

/** Card-view grouping (WI 31103 + 31108): retired seats (only present when the
 * Show-retired toggle opted in) render as their own greyed group AFTER the
 * dead collapse; dead-not-retired seats collapse to the bottom (code-tim
 * ruling 2026-08-29). Retirement outranks liveness here: a retired seat is
 * usually also dead, and it must land in `retired`, never in `dead`, so the
 * pane and the 31115 orphan cleanup agree on what retired-vs-orphan means.
 * All groups preserve the incoming buildSeatRows order. */
export function buildCardGroups(rows: SeatRow[]): {
  main: SeatRow[];
  dead: SeatRow[];
  retired: SeatRow[];
} {
  return {
    main: rows.filter((r) => !r.retired && r.band !== 'dead'),
    dead: rows.filter((r) => !r.retired && r.band === 'dead'),
    retired: rows.filter((r) => r.retired),
  };
}

/** Roll display rows up into the headline numbers the summary strip shows.
 * Retired rows are EXCLUDED from every fleet number (they only appear at all
 * when the toggle is on, and "Seats" must not grow because a lens opened);
 * they get their own count instead. */
export function summarizeQueue(rows: SeatRow[]): QueueSummary {
  const fleet = rows.filter((r) => !r.retired);
  return {
    totalSeats: fleet.length,
    activeSeats: fleet.filter((r) => r.band === 'active').length,
    availableSeats: fleet.filter((r) => r.band === 'available').length,
    seatsInDisagreement: fleet.filter((r) => r.hasDisagreement).length,
    deadSeatsOwingBlocking: fleet.filter(
      (r) => !r.modelAlive && r.blockingOwedCount > 0
    ).length,
    retiredSeats: rows.length - fleet.length,
  };
}
